import React, { useCallback, useMemo } from 'react';
import { Box, Text, FormControl, FormLabel, Flex, Tooltip, Menu, MenuButton, MenuOptionGroup, MenuItemOption, MenuList, Button, Portal } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { keyframes } from '@emotion/react';
import { pxToRem } from '@codametrix/themes/chakra/biome';
import { DelayedInputBox } from '@codametrix/ui-components';
import './styles/index.scss';
const bounce = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.6); }
  40% { opacity: 1; transform: scale(1); }
`;

const ThreeDotLoader: React.FC = () => (
  <Flex align="center" gap="3px" ml={2} aria-label="Evaluating">
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        w="5px"
        h="5px"
        borderRadius="50%"
        bg="gray.400"
        sx={{ animation: `${bounce} 1.2s ease-in-out ${i * 0.16}s infinite` }}
      />
    ))}
  </Flex>
);

const formatSpanOption = (span: Accelerate.SpanAnnotation, allSpans: Accelerate.SpanAnnotation[]): string => {
  const maxLen = 80;
  const text = span.coveredText || '(empty span)';
  const truncated = text.length > maxLen ? text.substring(0, maxLen) + '...' : text;

  // Check if another span has the same truncated text — disambiguate with offset
  const hasDuplicate = allSpans.some(
    s => s.id !== span.id && (s.coveredText || '(empty span)').substring(0, maxLen) === text.substring(0, maxLen)
  );

  if (hasDuplicate) {
    return `${truncated} (${span.beginOffset}-${span.endOffset})`;
  }
  return truncated;
};

const isAnswerActiveForQuestion = (
  answer: CQA.AnswerValue,
  inclusionMode?: CQA.InclusionMode,
  selectedAnswerIds?: number[]
): boolean => {
  if (answer.activeForQuestion != null) {
    return answer.activeForQuestion;
  }
  if (answer.id == null || !inclusionMode || !Array.isArray(selectedAnswerIds)) {
    return true;
  }
  if (inclusionMode === 'INCLUDE') {
    return selectedAnswerIds.includes(answer.id);
  }
  return !selectedAnswerIds.includes(answer.id);
};

export interface RubricQuestionFieldProps {
  workflowQuestion: CQA.WorkflowQuestion;
  currentAnswer?: CQA.Attribute;
  /** value: number = answerValueId (MULTIPLE_CHOICE/SYSTEM), number[] = spanAnnotationIds (SPAN/SPANS), string = answerDisplayText (FREE_TEXT) */
  onAnswerChange: (value: number | number[] | string) => void;
  isDisabled?: boolean;
  isAutofilled?: boolean;
  /** If provided, only show these answer options (used by SET_ANSWERS triggers) */
  filteredAnswerIds?: number[];
  /** When true, shows a three-dot loader to the right of the input */
  isEvaluating?: boolean;
  /** All span annotations in the case — required for SPAN question type */
  spans?: Accelerate.SpanAnnotation[];
}

/**
 * Renders a single rubric question with its answer options.
 * Currently supports MULTIPLE_CHOICE questions rendered as dropdowns.
 */
export const RubricQuestionField: React.FC<RubricQuestionFieldProps> = ({
  workflowQuestion,
  currentAnswer,
  onAnswerChange,
  isDisabled = false,
  isAutofilled = false,
  filteredAnswerIds,
  isEvaluating = false,
  spans,
}) => {
  const effectiveDisabled = isDisabled || isAutofilled;
  const { question } = workflowQuestion;
  const labelText = `${question.questionText || question.name}${isAutofilled ? ' (Autofilled)' : ''}`;

  // Compute visible answers - filter by filteredAnswerIds if provided (SET_ANSWERS trigger)
  const visibleAnswers = useMemo(() => {
    const answers = question.answers || [];
    const activeAnswers = answers.filter(
      answer => answer.active !== false
        && isAnswerActiveForQuestion(answer, question.inclusionMode, question.selectedAnswerIds)
    );

    if (filteredAnswerIds && filteredAnswerIds.length > 0) {
      return activeAnswers.filter(a => filteredAnswerIds.includes(a.id));
    }
    return activeAnswers;
  }, [question.answers, question.inclusionMode, question.selectedAnswerIds, filteredAnswerIds]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = parseInt(e.target.value, 10);
      if (!isNaN(value)) {
        onAnswerChange(value);
      }
    },
    [onAnswerChange]
  );

  // Get current value - use explicit null check for clarity
  const currentValue = currentAnswer?.answerValueId != null ? currentAnswer.answerValueId.toString() : '';

  // Render based on question type
  // Both MULTIPLE_CHOICE and SYSTEM question types render as dropdowns
  if (question.questionType === 'MULTIPLE_CHOICE' || question.questionType === 'SYSTEM') {
    return (
      <Tooltip
        label="Question autofilled by previous answers"
        isDisabled={!isAutofilled}
        placement="top"
        hasArrow
      >
        <FormControl size="sm" isRequired={question.required} isDisabled={effectiveDisabled}>
          <FormLabel fontSize={pxToRem(12)} mb={1} color="gray.600">
            {labelText}
          </FormLabel>
          <Flex align="center">
            <select
              className="form-select"
              value={currentValue}
              onChange={handleChange}
              disabled={effectiveDisabled}
              style={{ flex: 1 }}
            >
              <option value="" disabled>
                Select an option
              </option>
              {visibleAnswers.map(answer => (
                <option key={answer.id} value={answer.id}>
                  {answer.displayText || answer.name}
                </option>
              ))}
            </select>
            {isEvaluating && <ThreeDotLoader />}
          </Flex>
        </FormControl>
      </Tooltip>
    );
  }

  // FREE_TEXT: use DelayedInputBox (debounced save, blur save, Enter submit, Shift+Enter new line)
  if (question.questionType === 'FREE_TEXT') {
    return (
      <Tooltip
        label="Question autofilled by previous answers"
        isDisabled={!isAutofilled}
        placement="top"
        hasArrow
      >
        <Flex align="flex-end">
          <Box flex={1}>
            <DelayedInputBox
              label={labelText}
              value={currentAnswer?.answerDisplayText ?? ''}
              onSave={(trimmed) => onAnswerChange(trimmed)}
              isRequired={question.required}
              isDisabled={effectiveDisabled}
            />
          </Box>
          {isEvaluating && <ThreeDotLoader />}
        </Flex>
      </Tooltip>
    );
  }

  // SPAN: render a single-select dropdown of span annotations
  if (question.questionType === 'SPAN') {
    const spanList = spans || [];
    const liveSpanIds = new Set(spanList.map(span => span.id));
    const selectedId = (currentAnswer?.spanAnnotationIds ?? []).find(id => liveSpanIds.has(id));

    return (
      <FormControl size="sm" isRequired={question.required} isDisabled={isDisabled}>
        <FormLabel fontSize={pxToRem(12)} mb={1} color="gray.600">
          {question.questionText || question.name}
        </FormLabel>
        <Flex align="center">
          <select
            className="form-select"
            value={selectedId != null ? selectedId.toString() : ''}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val)) {
                onAnswerChange([val]);
              }
            }}
            disabled={isDisabled || spanList.length === 0}
            style={{ flex: 1 }}
          >
            <option value="" disabled>
              {spanList.length === 0 ? 'No spans available' : 'Select a span'}
            </option>
            {spanList.map(span => (
              <option key={span.id} value={span.id.toString()}>
                {formatSpanOption(span, spanList)}
              </option>
            ))}
          </select>
          {isEvaluating && <ThreeDotLoader />}
        </Flex>
      </FormControl>
    );
  }

  // SPANS: render a multi-select dropdown of span annotations
  if (question.questionType === 'SPANS') {
    const spanList = spans || [];
    const liveSpanIds = new Set(spanList.map(span => span.id));
    const selectedIds = (currentAnswer?.spanAnnotationIds ?? []).filter(id => liveSpanIds.has(id));
    const selectedStrings = selectedIds.map(id => id.toString());

    // Build display label for the button
    const selectedCount = selectedIds.length;
    const buttonLabel = spanList.length === 0
      ? 'No spans available'
      : selectedCount === 0
        ? 'Select spans'
        : `${selectedCount} span${selectedCount > 1 ? 's' : ''} selected`;

    return (
      <FormControl size="sm" isRequired={question.required} isDisabled={isDisabled}>
        <FormLabel fontSize={pxToRem(12)} mb={1} color="gray.600">
          {question.questionText || question.name}
        </FormLabel>
        <Flex align="center">
          <Menu closeOnSelect={false}>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              size="sm"
              variant="outline"
              flex={1}
              textAlign="left"
              fontWeight="normal"
              isDisabled={isDisabled || spanList.length === 0}
            >
              {buttonLabel}
            </MenuButton>
            <Portal>
              <MenuList maxH="250px" overflowY="auto" zIndex={1400}>
                <MenuOptionGroup
                  type="checkbox"
                  value={selectedStrings}
                  onChange={(values) => {
                    const ids = (values as string[]).map(v => parseInt(v, 10));
                    onAnswerChange(ids);
                  }}
                >
                  {spanList.map(span => (
                    <MenuItemOption key={span.id} value={span.id.toString()}>
                      {formatSpanOption(span, spanList)}
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              </MenuList>
            </Portal>
          </Menu>
          {isEvaluating && <ThreeDotLoader />}
        </Flex>
      </FormControl>
    );
  }

  // Fallback for unsupported question types
  return (
    <Box>
      <Text fontSize={pxToRem(12)} color="gray.500">
        Unsupported question type: {question.questionType}
      </Text>
    </Box>
  );
};

export default RubricQuestionField;
